/*
 * The PDF reader (x-pdf, owner 2026-10-20; docs/design/media.md, docs/architecture/media.md): the document's pages as
 * sheets, drawn by PDF.js's engine (the copy in public/pdf, loaded when a reader is first on screen) with their text
 * laid over them (selectable, found by the browser's Find, read by screen readers). As PDF.js's own viewer: pages are
 * drawn one at a time as they come near, nearest first, and only a few nearest stay drawn; pinched in, the part in
 * view gets a sharp patch. The toolbar shows the page in view and floats (inset, rounded) once the pages carry it down.
 * Read as a book (the default: the pages side by side, swiped, dragged or stepped with go(), "near" and "in view"
 * measured across) or as a scroll (down); the choice is kept in this browser. Contained, it scrolls inside its own
 * box, which is then what "near" and "in view" are measured in. Everything it starts stops when it leaves the page.
 */
const VIEW = 'pdf-view';
// The PDF.js copy in public/pdf: its files keep their names between versions, so the version goes in their addresses
// and an upgrade (change it here) is never mixed with an old copy a browser kept (PDF.js refuses mismatched files)
const PDFJS = '5.2.133';
const stored = (key) => { try { return localStorage.getItem(key); } catch { return null; } };
// fn at most once a frame, however often it's asked for (scrolling fires far more often)
const onFrame = (fn) => {
    let asked = false;
    return () => asked || (asked = true, requestAnimationFrame(() => (asked = false, fn())));
};

// Where each reader opening Full Screen was (its dialog's id => page and how far down it), for the one inside to open at
const opening = {};

Alpine.data('pdfReader', (url, filename, contained = false, {view = 'book', gaps = false, switchable = true, dialog = null, toolbar = true} = {}) => ({
    state: 'loading',   // loading, ready, failed
    count: 0,
    page: 1,
    // How this place opens it (x-pdf's view), unless the reader chose otherwise while they may switch
    book: ((switchable && stored(VIEW)) || view) !== 'scroll',
    gaps,   // pages 2px apart (x-pdf's gaps, on by default)
    watch: null,   // (re)starts the observers for the way it's read
    fit: null,   // sets a book's height to the page in view's
    stop: null,   // ends everything it started (destroy)
    contained,
    stuck: false,   // the toolbar has left its place, carried down over the pages, and floats
    dragging: false,   // a book's page being dragged with the mouse
    height: null,   // a book's height: the page in view's, so a smaller page leaves no space below it
    zoomed: false,   // the screen pinched in (a phone): a book stops taking sideways drags, so they move around the page
    ratio: 1 / 1.414,   // the first page's width over its height: in Full Screen, how wide a page fits the screen's height

    /** The toolbar joined to the pages, square where they meet, until it floats (and while it loads); none: rounded */
    get attached() {
        return toolbar && this.state !== 'failed' && !this.stuck;
    },

    async init() {
        // What it starts, each a function that ends it: listeners, observers, the document
        const ends = [];
        this.stop = () => ends.forEach((end) => end());

        // The toolbar floats the moment it starts moving down with the pages: it has left the mark at its own place.
        // Any scroll moves it (the page's, a phone's inner one, Full Screen's, its own box's); a reader not shown (a
        // closed dialog: no size) never floats. Its own mark and bar, the first in it: $refs would also find those of
        // the reader behind Full Screen, scrolled away
        if (toolbar) {
            const mark = this.$root.querySelector('[x-ref="mark"]');
            const bar = this.$root.querySelector('[x-ref="bar"]');
            const float = onFrame(() => {
                const place = mark.getBoundingClientRect();
                this.stuck = place.width > 0 && bar.getBoundingClientRect().top > place.top + 1;
            });
            document.addEventListener('scroll', float, {capture: true, passive: true});
            window.addEventListener('resize', float, {passive: true});
            ends.push(() => document.removeEventListener('scroll', float, {capture: true}), () => window.removeEventListener('resize', float));
            this.$nextTick(float);
        }

        try {
            const pdfjs = await import(/* @vite-ignore */ `/pdf/build/pdf.mjs?v=${PDFJS}`);
            pdfjs.GlobalWorkerOptions.workerSrc = `/pdf/build/pdf.worker.mjs?v=${PDFJS}`;

            // Kept here, not on `this`: PDF.js's objects mustn't be wrapped in Alpine's reactive proxy. Its fonts, character
            // maps, JPEG 2000 decoder and colour profiles come from the same copy; no code is built from the document
            const task = pdfjs.getDocument({
                url,
                cMapUrl: '/pdf/web/cmaps/',
                cMapPacked: true,
                standardFontDataUrl: '/pdf/web/standard_fonts/',
                wasmUrl: '/pdf/web/wasm/',
                iccUrl: '/pdf/web/iccs/',
                isEvalSupported: false,
            });
            let observers = [];   // the way it's read's (watch() swaps them)
            ends.push(() => observers.forEach((observer) => observer.disconnect()), () => task.destroy());
            const doc = await task.promise;
            const pages = await Promise.all(Array.from({length: doc.numPages}, (_, i) => doc.getPage(i + 1)));
            const drawn = new Map();   // slot => {task: its render, viewport: the page's size then}

            this.count = pages.length;
            this.state = 'ready';
            this.$dispatch('pdf-loaded');   // for the place around it (the print dialog's buttons wait for it)
            await this.$nextTick();

            const slots = [...this.$refs.pages.querySelectorAll('.pdf-page')];
            slots.forEach((slot, i) => {
                const {width, height} = pages[i].getViewport({scale: 1});
                slot.style.aspectRatio = `${width} / ${height}`;
                i === 0 && (this.ratio = width / height);
            });

            // A drawing's most pixels: PDF.js's own viewer's limit on phones, past which their graphics garble letters
            // (seen on an Android phone at 16 million)
            const MOST_PIXELS = 5242880;
            const density = () => window.devicePixelRatio || 1;   // sharp on high-density screens
            // A drawing width × height CSS pixels at `sharp` device pixels to each, for a page's own or a patch
            const canvasFor = (width, height, sharp, className) => {
                const canvas = Object.assign(document.createElement('canvas'), {width: Math.ceil(width * sharp), height: Math.ceil(height * sharp), className});
                canvas.setAttribute('aria-hidden', 'true');
                return canvas;
            };

            // A page drawn at the screen's sharpness, off screen and then swapped for the old drawing, so it never
            // blinks; its text laid over it (kept while the page's width is the same)
            const draw = async (slot) => {
                const i = slots.indexOf(slot);
                drawn.get(slot)?.task.cancel();

                const viewport = pages[i].getViewport({scale: slot.clientWidth / pages[i].getViewport({scale: 1}).width});
                const sharp = Math.min(density(), Math.sqrt(MOST_PIXELS / (viewport.width * viewport.height)));
                const canvas = canvasFor(viewport.width, viewport.height, sharp, 'block h-full w-full');

                const task = pages[i].render({canvasContext: canvas.getContext('2d'), viewport, transform: sharp === 1 ? null : [sharp, 0, 0, sharp, 0, 0]});
                drawn.set(slot, {task, viewport});
                try {
                    await task.promise;
                } catch {
                    return;   // cancelled by a newer draw
                }

                const old = slot.querySelector('canvas:not(.pdf-detail)');
                old ? old.replaceWith(canvas) : slot.prepend(canvas);
                const width = String(Math.round(viewport.width));
                if (slot.querySelector('.textLayer')?.dataset.width === width) return;

                slot.querySelector('.pdf-detail')?.remove();   // drawn for the old width
                slot.querySelector('.textLayer')?.remove();
                slot.style.setProperty('--total-scale-factor', viewport.scale);
                const layer = Object.assign(document.createElement('div'), {className: 'textLayer'});
                layer.dataset.width = width;
                slot.append(layer);
                await new pdfjs.TextLayer({textContentSource: pages[i].streamTextContent(), container: layer, viewport}).render();
            };

            // Pinched in (a phone): a drag moves around the zoomed page rather than turning it (the arrows still turn
            // it), and once it's still, the part of each page in view is drawn again as sharp as the zoom, a patch laid
            // exactly over the page's own drawing (under its text). Only what's in view, so however far it's zoomed the
            // patch stays screen-sized. Moving on, the edges show the page's own drawing until it's still again;
            // zoomed out, the patches go
            const patches = new Map();   // slot => its patch's render task
            const patch = async (slot) => {
                const {viewport} = drawn.get(slot) ?? {};
                if (!viewport || !slot.isConnected) return;
                const box = slot.getBoundingClientRect();
                const view = window.visualViewport;
                // The page's part in view: the zoomed view, inside the reader's own box (a book shows one page; a
                // contained reader scrolls in its own), in the page's own CSS pixels
                const frame = (contained ? this.$root : this.$refs.pages).getBoundingClientRect();
                const left = Math.max(box.left, view.offsetLeft, frame.left), right = Math.min(box.right, view.offsetLeft + view.width, frame.right);
                const top = Math.max(box.top, view.offsetTop, frame.top), bottom = Math.min(box.bottom, view.offsetTop + view.height, frame.bottom);
                if (right - left < 1 || bottom - top < 1) {
                    unpatch(slot);
                    return;
                }
                const [x, y, width, height] = [left - box.left, top - box.top, right - left, bottom - top];
                const sharp = Math.min(density() * view.scale, Math.sqrt(MOST_PIXELS / (width * height)));

                patches.get(slot)?.cancel();
                const canvas = canvasFor(width, height, sharp, 'pdf-detail pointer-events-none absolute');
                Object.assign(canvas.style, {left: `${x}px`, top: `${y}px`, width: `${width}px`, height: `${height}px`});
                const task = pages[slots.indexOf(slot)].render({canvasContext: canvas.getContext('2d'), viewport, transform: [sharp, 0, 0, sharp, -x * sharp, -y * sharp]});
                patches.set(slot, task);
                try {
                    await task.promise;
                } catch {
                    return;   // cancelled by a newer patch
                }

                const old = slot.querySelector('.pdf-detail');
                old ? old.replaceWith(canvas) : slot.querySelector('canvas')?.after(canvas);
            };
            const unpatch = (slot) => {
                patches.get(slot)?.cancel();
                patches.delete(slot);
                slot.querySelector('.pdf-detail')?.remove();
            };

            let settling;
            const settle = () => {
                clearTimeout(settling);
                settling = setTimeout(() => new Set([...drawn.keys(), ...patches.keys()]).forEach((slot) => this.zoomed ? patch(slot) : unpatch(slot)), 250);
            };
            const pinched = () => {
                this.zoomed = (window.visualViewport?.scale ?? 1) > 1.05;
                settle();
            };
            window.visualViewport?.addEventListener('resize', pinched);
            window.visualViewport?.addEventListener('scroll', settle);
            document.addEventListener('scroll', settle, {capture: true, passive: true});
            ends.push(
                () => clearTimeout(settling),
                () => patches.forEach((task) => task.cancel()),
                () => window.visualViewport?.removeEventListener('resize', pinched),
                () => window.visualViewport?.removeEventListener('scroll', settle),
                () => document.removeEventListener('scroll', settle, {capture: true}),
            );

            // As PDF.js's own viewer does: pages are drawn one at a time, the one nearest the middle of the view first
            // (or drawn again when its width changed), and only the KEEP nearest stay drawn; the others give their
            // drawing back (white, their text kept, so Find still finds it) until they come near again. However long
            // the document, a phone holds a few pages' drawings
            const KEEP = 6;
            const near = new Set();   // the pages within a screen of view (a book: a page)
            let drawing = false;
            const distance = (slot) => {
                const box = slot.getBoundingClientRect();
                const [x, y] = [window.innerWidth / 2, window.innerHeight / 2];
                return Math.hypot(Math.max(box.left - x, x - box.right, 0), Math.max(box.top - y, y - box.bottom, 0));
            };
            const release = (slot) => {
                drawn.get(slot).task.cancel();
                drawn.delete(slot);
                slot.querySelector('canvas:not(.pdf-detail)')?.remove();
                unpatch(slot);
            };
            const stale = (slot) => !drawn.has(slot) || Math.abs(drawn.get(slot).viewport.width - slot.clientWidth) >= 2;
            const queue = async () => {
                if (drawing) return;
                const next = [...near].filter(stale).sort((a, b) => distance(a) - distance(b))[0];
                if (!next) {
                    // All near ones drawn: the farthest of the rest give theirs back, past KEEP
                    [...drawn.keys()].filter((slot) => !near.has(slot)).sort((a, b) => distance(a) - distance(b))
                        .slice(Math.max(KEEP - near.size, 0)).forEach(release);
                    return;
                }
                drawing = true;
                try {
                    await draw(next);
                } finally {
                    drawing = false;
                }
                queue();
            };

            // Near as they come within a screen (a book: a page) of view; the page in view is the one crossing the
            // middle (a book: the one mostly across, by the scroll position, as the box cuts off a page taller than it)
            this.watch = () => {
                observers.forEach((observer) => observer.disconnect());
                near.clear();
                const root = this.book ? this.$refs.pages : (contained ? this.$el : null);
                const nearing = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => entry.isIntersecting ? near.add(entry.target) : near.delete(entry.target));
                    queue();
                }, {root, rootMargin: this.book ? '0px 100%' : '100% 0px'});
                slots.forEach((slot) => nearing.observe(slot));
                if (this.book) {
                    const strip = this.$refs.pages;
                    const turned = () => {
                        const step = slots[1] ? slots[1].parentElement.offsetLeft - slots[0].parentElement.offsetLeft : 1;
                        this.page = Math.min(Math.max(Math.round(strip.scrollLeft / step) + 1, 1), slots.length);
                    };
                    strip.addEventListener('scroll', turned, {passive: true});
                    observers = [nearing, {disconnect: () => strip.removeEventListener('scroll', turned)}];
                } else {
                    const middle = new IntersectionObserver((entries) => entries.filter((entry) => entry.isIntersecting).forEach((entry) => this.page = slots.indexOf(entry.target) + 1), {root, rootMargin: '-50% 0px'});
                    slots.forEach((slot) => middle.observe(slot));
                    observers = [nearing, middle];
                }
            };
            this.watch();

            // A page at a new width (the screen, or the way it's read, changed): the near ones drawn again at it, the
            // others when they come near
            const resized = new ResizeObserver(() => queue());
            slots.forEach((slot) => resized.observe(slot));

            // A book is as tall as the page in view, growing or shrinking as the pages turn
            this.fit = () => this.height = this.book ? slots[this.page - 1]?.offsetHeight || null : null;
            this.$watch('page', () => this.fit());
            const refit = new ResizeObserver(() => this.fit());
            refit.observe(this.$refs.pages);
            ends.push(() => resized.disconnect(), () => refit.disconnect());
            this.fit();

            // The reader in Full Screen opens where the page's own was: the same page, in a scroll the same point on it
            // (once the dialog has laid it out: two frames)
            if (dialog && opening[dialog]) {
                const {page, along} = opening[dialog];
                await new Promise((done) => requestAnimationFrame(() => requestAnimationFrame(done)));
                this.go(page, false);
                if (!this.book && along) {
                    const slide = this.$refs.pages.querySelectorAll('.pdf-slide')[page - 1];
                    (this.$el.closest('dialog') ?? window).scrollBy(0, along * slide.offsetHeight);
                }
            }
        } catch {
            this.state = 'failed';
            this.$dispatch('pdf-loaded');
        }
    },

    destroy() {
        this.stop?.();
    },

    /** Full Screen (its dialog, x-pdf): opened where this reader is, the page in view and, in a scroll, how far down it */
    openFull(id) {
        const slides = [...this.$refs.pages.querySelectorAll('.pdf-slide')];
        // A scroll: the page at the top of the view (just under the toolbar, as go() shows one) and how far down it
        const top = (contained ? this.$el.getBoundingClientRect().top : 0) + (slides[0] ? parseFloat(getComputedStyle(slides[0]).scrollMarginTop) || 0 : 0);
        const index = this.book ? this.page - 1 : Math.max(slides.findIndex((slide) => slide.getBoundingClientRect().bottom > top), 0);
        const slide = slides[index];
        const along = !this.book && slide ? Math.min(Math.max((top - slide.getBoundingClientRect().top) / slide.offsetHeight, 0), 1) : 0;
        opening[id] = {page: index + 1, along};
        this.$dispatch(id, 'open');
    },

    /** Shows page n: a book turns to it, a scroll goes down to it. */
    go(n, smooth = true) {
        n = Math.min(Math.max(n, 1), this.count);
        const slide = this.$refs.pages.querySelectorAll('.pdf-slide')[n - 1];
        if (!slide) return;
        const behavior = smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto';
        if (this.book) {
            const pages = this.$refs.pages;
            pages.scrollTo({left: pages.scrollLeft + slide.getBoundingClientRect().left - pages.getBoundingClientRect().left, behavior});
        } else {
            slide.scrollIntoView({block: 'start', behavior});
        }
    },

    /**
     * A book's pages dragged with the mouse (or a pen), like a finger swipes them (that's the browser's own): the page
     * follows the pointer, and a drag of more than 40px turns to the next or previous page, less springs back.
     */
    drag(event) {
        if (!this.book || event.pointerType === 'touch' || event.button !== 0) return;
        event.preventDefault();   // no text selected while dragging

        const pages = this.$refs.pages;
        const start = event.clientX;
        const left = pages.scrollLeft;
        const from = this.page;
        this.dragging = true;   // snapping off, or it fights the pointer

        const move = (e) => pages.scrollLeft = left - (e.clientX - start);
        const up = (e) => {
            ['pointermove', 'pointerup', 'pointercancel'].forEach((type) => window.removeEventListener(type, type === 'pointermove' ? move : up));
            const moved = e.clientX - start;
            this.go(Math.abs(moved) > 40 ? from - Math.sign(moved) : from);
            // Snapping back on once it has settled
            const settle = () => this.dragging = false;
            pages.addEventListener('scrollend', settle, {once: true});
            setTimeout(settle, 700);
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
        window.addEventListener('pointercancel', up);
    },

    /**
     * The left and right arrow keys turn a book's pages anywhere on the page while it's on screen, unless something
     * else takes them: typing in a field, a dialog over it, or a modifier key held.
     */
    key(event, step) {
        if (!this.book || this.state !== 'ready' || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (event.target.closest?.('input, textarea, select, [contenteditable]:not([contenteditable="false"])')) return;
        if (document.querySelector('dialog[open]') && !this.$el.closest('dialog[open]')) return;
        const box = this.$refs.pages.getBoundingClientRect();
        if (box.bottom <= 0 || box.top >= window.innerHeight) return;
        event.preventDefault();
        this.go(this.page + step);
    },

    /** Reads it as a book or a scroll, staying on the same page, and remembers the choice. */
    async read(book) {
        const page = this.page;
        this.book = book;
        try { localStorage.setItem(VIEW, book ? 'book' : 'scroll'); } catch {}
        await this.$nextTick();
        this.watch?.();
        this.fit?.();
        if (page > 1) this.go(page, false);
    },

    /** Download, under the document's own name (download.js) */
    save() {
        window.download(url, filename);
    },
}));
